import {
  defaultThankYou,
  type CartMode,
  type FormVisibility,
  type ThankYouConfig,
} from '@cod/config-schema';
import { Box, Button, Card, Icon, Modal, Tag, Text, Title, useToast } from '@nimbus-ds/components';
import { ChevronDownIcon, ChevronUpIcon, InfoCircleIcon } from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TextAreaControl, TextControl, ToggleControl } from '../components/controls.js';
import { ScaleToFit } from '../designer/ScaleToFit.js';
import { useAppState } from '../state/AppState.js';
import { useUnsavedChanges } from '../state/NavigationGuard.js';
import {
  buildConfirmationTemplate,
  detectConfirmationRecipe,
  detectConfirmationTemplate,
  templateLocale,
  type TemplateLocale,
  CONFIRMATION_TEMPLATE_IDS,
  CUSTOM_TEMPLATE_CHOICE,
  type ConfirmationTemplateId,
  type TemplateChoice,
} from './confirmationTemplates.js';
import { validateThankYou } from './thankYou.model.js';

const VISIBILITY_OPTIONS: FormVisibility[] = ['disabled', 'cart', 'product', 'both'];
const OPTION_LABEL: Record<FormVisibility, string> = {
  disabled: 'settings.visDisabled',
  cart: 'settings.visCart',
  product: 'settings.visProduct',
  both: 'settings.visBoth',
};

/**
 * Segmented button control, styled to match the app's card/picker look (same palette as the
 * designer's ModeSwitch). Plain buttons so the whole segment is clickable. Wraps on narrow
 * widths. Used by every picker on this page.
 *
 * 🚨 ALWAYS full width, with the options in equal segments — this was an opt-in `fullWidth` prop
 * and the three pickers in the thank-you card never passed it, so they hugged their labels while
 * the visibility and cart-mode pickers above them spanned the card. Two shapes of the same
 * control on one page reads as two different kinds of control. The prop is gone rather than
 * defaulted to `true`, so the next picker cannot reintroduce the difference by omission.
 */
function Segmented<T extends string>({
  value,
  options,
  onSelect,
}: {
  value: T;
  options: { value: T; label: string }[];
  onSelect: (next: T) => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        // 🚨 With `width: 100%`, the 4px padding and 1px border are ADDED to it under the default
        // `content-box` — so the control measured its card + 10px and was sliced on every phone.
        boxSizing: 'border-box',
        flexWrap: 'wrap',
        gap: 4,
        padding: 4,
        background: '#ffffff',
        border: '1px solid #e3e3e6',
        borderRadius: 10,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(opt.value)}
            style={{
              flex: '1 1 0',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 16px',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 14,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              background: active ? '#d4d4d6' : 'transparent',
              color: active ? '#1a1a1a' : '#5a5a5e',
              transition: 'background-color .12s ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** A chevron toggle that shows/hides a block. Keeps the thank-you card scannable: the template
 * gallery and the raw HTML are each one click away rather than stacked on top of each other.
 *
 * The trigger stays a native `button` for the semantics Nimbus has no component for (a
 * disclosure is not a Link, and `Collapsible` ships only the panel, no styled header), but its
 * label uses Nimbus tokens — `primary-interactive` at body size — so it reads as a control
 * rather than the caption-grey it started as. */
function Disclosure({
  open,
  onToggle,
  label,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box display="flex" flexDirection="column" gap="2">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          alignSelf: 'flex-start',
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <Icon
          color="primary-interactive"
          source={open ? <ChevronUpIcon size="medium" /> : <ChevronDownIcon size="medium" />}
        />
        <Text fontSize="base" fontWeight="medium" color="primary-interactive">
          {label}
        </Text>
      </button>
      {open && children}
    </Box>
  );
}

// Sample values so the preview reflects what a real order fills in for each placeholder.
const PREVIEW_TOKENS: Record<string, string> = {
  order_id: '987654',
  order_number: '1234',
  order_total: '$25.000',
  order_subtotal: '$25.000',
  full_name: 'Juan Pérez',
  first_name: 'Juan',
  last_name: 'Pérez',
  phone: '+57 300 123 4567',
  email: 'juan@example.com',
  full_address: 'Calle 1 #2-3, Bogotá',
  address: 'Calle 1 #2-3',
  address2: 'Apto 401',
  province: 'Cundinamarca',
  city: 'Bogotá',
  zip_code: '110111',
  order_note: '',
  shipping_rate_name: 'Envío a domicilio',
  page_url: 'https://tienda.com/producto',
  products_summary_with_quantity: 'Producto x 1',
  product_title: 'Producto',
  product_quantity: '1',
};

function fillPreview(template: string): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => PREVIEW_TOKENS[k] ?? m);
}

/**
 * The width a buyer's confirmation actually gets.
 *
 * `.cod-modal { max-width: 460px }` on the legacy path and `styles.panel`'s `width: 460px` on the
 * SDK one, less the form's own `padding: 0 20px 20px` and the thank-you panel's `20px 8px`. A
 * merchant designing against the full width of a desktop settings card is designing for a column
 * that does not exist.
 */
const PREVIEW_PANEL_WIDTH = 460 - 40 - 16;

/**
 * The rules the two storefront bundles apply to a merchant's page, restated for the preview.
 *
 * 🚨 **A merchant's page is usually EMAIL HTML — a 600px table that cannot fit the form.** Left
 * alone it lays out at 600px, which is what the storefront reported as a card hanging over the
 * page; both bundles now reflow it (`styles.ts`'s `.cod-thankyou-page table`, and `tableNode` /
 * `FIT` in `form-tree/src/htmlTree.ts`). Without the same rules here the preview would keep
 * showing the design at a width no buyer is ever given, which is exactly how the storefront bug
 * got as far as a live store: this box looked right.
 */
const PREVIEW_CSS = `
.cod-ty-preview { text-align: left; line-height: 1.5; overflow-wrap: anywhere; }
.cod-ty-preview, .cod-ty-preview * { box-sizing: border-box; }
.cod-ty-preview table { width: 100% !important; max-width: 100% !important; }
.cod-ty-preview * { max-width: 100%; }
.cod-ty-preview img { max-width: 100%; height: auto; }
`;

/**
 * The rendered thank-you page, with the shortcodes filled in from `PREVIEW_TOKENS`.
 *
 * Laid out at the buyer's own panel width (`PREVIEW_PANEL_WIDTH`) rather than the settings card's,
 * so what the merchant approves here is the reflow their buyers get — and scaled down rather than
 * reflowed again where the card is narrower, which is the same rule and the same component the
 * Designer's live preview uses (`ScaleToFit`). Reflowing twice would show a layout no buyer gets.
 *
 * ⚠️ This is the LEGACY fidelity — `innerHTML` in a DOM, which is exactly what the legacy bundle
 * does. A buyer on an SDK theme gets `htmlToTree` instead, which rebuilds the page out of NubeSDK
 * components; `confirmationTemplates.test.ts` is what keeps the shipped designs converting cleanly,
 * but hand-written HTML has no such guard, so an exotic tag can look right here and degrade there.
 */
function PreviewBox({ html }: { html: string }): JSX.Element {
  return (
    <div
      style={{
        border: '1px solid #e3e3e6',
        borderRadius: 8,
        padding: 16,
        background: '#fafafa',
      }}
    >
      <style>{PREVIEW_CSS}</style>
      <ScaleToFit reference={PREVIEW_PANEL_WIDTH} center>
        <div className="cod-ty-preview" dangerouslySetInnerHTML={{ __html: fillPreview(html) }} />
      </ScaleToFit>
    </div>
  );
}

/**
 * Clipboard write for "Copy HTML".
 *
 * The `execCommand` half is a real fallback, not belt and braces: this admin runs inside the
 * Nuvemshop iframe, and `navigator.clipboard` is gated on a `clipboard-write` permissions-policy
 * the parent frame does not have to delegate. It rejects rather than throwing synchronously, so
 * the failure is only visible from the `await`.
 *
 * Reports success rather than throwing, because the caller's whole response either way is which
 * toast to show.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* permissions-policy or an insecure context — try the older path */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    // Parked off-screen rather than hidden: `display:none` and `visibility:hidden` cannot be
    // selected, which is what this path copies from.
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch {
    return false;
  }
}

const TEMPLATE_LABEL: Record<ConfirmationTemplateId, string> = {
  '1-clean-minimal': 'settings.templateClean',
  '2-bold-banner': 'settings.templateBold',
  '3-receipt': 'settings.templateReceipt',
  '4-soft-friendly': 'settings.templateSoft',
  '5-delivery-steps': 'settings.templateSteps',
};

/**
 * Language options for the template copy. Labelled with ENDONYMS rather than translated names, so
 * a merchant picks out their buyers' language whatever the dashboard is set to — and so the three
 * labels don't change meaning when `admin_language` does. Spelled unaccented to match the header's
 * own language switcher (`LANGUAGE_LABELS` in Shell.tsx); the accented forms belong to the
 * buyer-facing template copy, which is a different audience.
 */
const TEMPLATE_LOCALE_OPTIONS: { value: TemplateLocale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Espanol' },
  { value: 'pt-BR', label: 'Portugues' },
];

/** Field-wise equality for a thank-you config. Exact — every field is a primitive. */
function thankYouEqual(a: ThankYouConfig, b: ThankYouConfig): boolean {
  return (Object.keys(defaultThankYou) as (keyof ThankYouConfig)[]).every((k) => a[k] === b[k]);
}

/**
 * Post-purchase "thank you" page settings. After a successful COD order the storefront either
 * shows custom HTML, redirects to a URL, or opens a WhatsApp chat. Persisted into the form
 * config JSON via a scoped read-modify-write, and edited in a local draft (Save button) since
 * it has several fields, unlike the single-toggle visibility control above.
 */
function ThankYouSection(): JSX.Element {
  const { t } = useTranslation();
  const { config, settings, saveThankYou } = useAppState();
  const { addToast } = useToast();
  const saved = config?.thank_you ?? defaultThankYou;
  const [draft, setDraft] = useState<ThankYouConfig>(saved);
  const [saving, setSaving] = useState(false);
  // Template picker state. All of it is picker-local: the chosen template is baked into
  // `page_html` on insert, so nothing here needs to persist.
  const [choice, setChoice] = useState<TemplateChoice>('1-clean-minimal');
  const [callToConfirm, setCallToConfirm] = useState(false);
  // null = "follow the store language". Kept as an override rather than seeded into state so the
  // default still resolves correctly when `settings` arrives after the first render, and so a
  // merchant's explicit pick is never stomped by that load.
  const [localeOverride, setLocaleOverride] = useState<TemplateLocale | null>(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [htmlOpen, setHtmlOpen] = useState(false);
  /**
   * Whether Save has been pressed on an invalid draft.
   *
   * Validation is not shown before that: a merchant who has just picked "button to a URL" has an
   * empty URL field by definition, and marking it red the instant the mode changes scolds them
   * for not having typed yet. After the press it re-derives on every keystroke, so the message
   * clears the moment the value is good instead of waiting for another click.
   */
  const [attemptedSave, setAttemptedSave] = useState(false);

  /**
   * Whether the merchant has driven the picker themselves in this session.
   *
   * 🚨 The restore below derives the picker from the saved page, which is right on load and wrong
   * afterwards. `saveThankYou` hands back a fresh `thank_you` object, so every Save re-ran it —
   * and a merchant who had selected "Your own HTML" without editing the HTML yet still had a page
   * that matched a template exactly, so the restore snapped the picker back onto that design and
   * took the editor away with it. An explicit choice outranks a derived one until the page
   * reloads.
   */
  const pickerTouched = useRef(false);

  /**
   * The stored page this draft was last synced from.
   *
   * 🚨 The effect below keys off `config.thank_you`'s OBJECT identity, and the two controls above
   * this card save immediately — each handing back a whole fresh config whose `thank_you` is
   * unchanged by value. So flipping visibility or the cart mode while editing this section threw
   * the merchant's unsaved page away, silently and without even the navigation guard noticing.
   * Comparing by value is what tells a real change from a new object carrying the same page.
   */
  const syncedFrom = useRef<ThankYouConfig | null>(null);

  // Reset the draft when the stored config (re)loads or is saved elsewhere.
  // 🚨 Layout effect, not `useEffect`: `dirty` below compares the draft against the stored config,
  // and the render in which the config first arrives still holds the pre-load draft. A passive
  // effect runs after paint, so that frame would flash "Unsaved changes" on a page the merchant
  // has not touched. Running before paint means the two are never observed out of step.
  useLayoutEffect(() => {
    if (!config?.thank_you) return;
    if (syncedFrom.current && thankYouEqual(syncedFrom.current, config.thank_you)) return;
    syncedFrom.current = config.thank_you;
    setDraft(config.thank_you);
    if (pickerTouched.current) return;
    // Point the picker at the page the merchant actually has. Its controls are local state with
    // fixed initial values, so without this a reload reopened the gallery on "Clean / store
    // language" and its preview read as though the saved template had reverted.
    const html = config.thank_you.page_html;
    const recipe = detectConfirmationRecipe(html);
    if (recipe) {
      setChoice(recipe.id);
      setLocaleOverride(recipe.locale);
      setCallToConfirm(recipe.callToConfirm);
      return;
    }
    // Hand-written HTML — or a template the merchant has since edited — has no recipe to restore.
    // It selects "Your own HTML", which is the only choice that shows the editor: reopening on a
    // design they never chose would leave their page with no surface to change it.
    if (html.trim()) setChoice(CUSTOM_TEMPLATE_CHOICE);
  }, [config?.thank_you]);

  const set = <K extends keyof ThankYouConfig>(key: K, value: ThankYouConfig[K]): void =>
    setDraft((d) => ({ ...d, [key]: value }));

  /**
   * The problem blocking a save. Derived from the draft, not stored, so it cannot go stale
   * against the values it describes.
   *
   * Shown after a Save attempt — or straight away when the STORED config is already broken, which
   * is the state this validation exists because merchants could reach. Nothing on this page said
   * so, and the merchant's live form has been dropping the handoff button ever since.
   */
  const problem = attemptedSave || validateThankYou(saved) ? validateThankYou(draft) : null;

  const onSave = async (): Promise<void> => {
    setAttemptedSave(true);
    // 🚨 Refuse a mode whose target is empty. Both renderers fall through to the merchant's page
    // HTML — or the generic panel — when `redirect_url` / `whatsapp_phone` is blank, so saving one
    // ships a store whose buyers get NO handoff button while the admin says a button is
    // configured. Blocking here rather than making the storefront louder: its tolerance is the
    // right behaviour for configs that already exist.
    const invalid = validateThankYou(draft);
    if (invalid) {
      // Toast as well as the inline message: the offending field can be scrolled off the top of a
      // long card, and a Save that appears to do nothing is the failure this is here to prevent.
      addToast({ id: 'save-thankyou', type: 'danger', text: t(invalid.message) });
      return;
    }
    setSaving(true);
    const ok = await saveThankYou(draft);
    setSaving(false);
    if (ok) setAttemptedSave(false);
    addToast({
      id: 'save-thankyou',
      type: ok ? 'success' : 'danger',
      text: ok ? t('common.saved') : t('common.saveError'),
    });
  };

  const modeOptions: { value: ThankYouConfig['mode']; label: string }[] = [
    { value: 'page', label: t('settings.thankYouModePage') },
    { value: 'url', label: t('settings.thankYouModeUrl') },
    { value: 'whatsapp', label: t('settings.thankYouModeWhatsapp') },
  ];

  // The buyer-facing store language picks the DEFAULT, not `admin_language` — the merchant may
  // browse this dashboard in a language their shoppers do not read. It is only a default because
  // we snapshot `main_language` at install and a store can sell in more languages than it
  // registered with, so the merchant gets the final say.
  const localeDefault = templateLocale(settings?.store_language ?? null);
  const locale = localeOverride ?? localeDefault;

  // "Your own HTML" is a mode, not a design: there is no template to build, preview, insert or
  // copy, so every control that acts on one hides behind this. What it reveals instead is the raw
  // editor below — deliberately the only way to reach it, so a merchant cannot half-edit a design
  // and be told it is still that design.
  const browsingTemplate = choice !== CUSTOM_TEMPLATE_CHOICE;
  const templateHtml = browsingTemplate
    ? buildConfirmationTemplate(choice, locale, callToConfirm)
    : '';

  const applyTemplate = (): void => {
    setDraft((d) => ({
      ...d,
      page_html: templateHtml,
      // Inserting a design overwrites the page — so stash it first if it was the merchant's own.
      // This is the moment their work would otherwise be destroyed: they replace a hand-written
      // page with a template, save, and the only copy is gone from the config.
      custom_page_html:
        d.page_html.trim() && !detectConfirmationTemplate(d.page_html)
          ? d.page_html
          : d.custom_page_html,
    }));
    setReplaceOpen(false);
    addToast({ id: 'apply-template', type: 'success', text: t('settings.templateApplied') });
  };

  // The raw template, placeholders and all — not the filled preview. A merchant copying a design
  // to build their own page needs `{order_total}` to still be `{order_total}`, not "$25.000".
  const copyTemplate = async (): Promise<void> => {
    const copied = await copyToClipboard(templateHtml);
    addToast({
      id: 'copy-template',
      type: copied ? 'success' : 'danger',
      text: copied ? t('settings.templateCopied') : t('settings.templateCopyFailed'),
    });
  };

  // Which template the editor currently holds — null once the merchant edits it by hand, which is
  // reported as "custom" rather than guessed at.
  const appliedTemplateId = detectConfirmationTemplate(draft.page_html);

  /**
   * The merchant's own page, which "Your own HTML" restores.
   *
   * Normally the stash, which is written when they type and when a template overwrites the page.
   * The fallback covers a page saved before that field existed: if what is in `page_html` is not
   * one of our designs, it is theirs, and it is the only copy there is.
   */
  const customPageHtml = draft.custom_page_html || (appliedTemplateId ? '' : draft.page_html);

  // Whether anything in this section is pending. Everything here edits a local draft behind an
  // explicit Save, and "Use this template" only writes that draft — so a merchant who has just
  // been told "Template inserted" reasonably reads the job as done and leaves without saving.
  // A Save that is inert until there IS something to save makes it a state indicator rather than
  // a button that always looks the same: greyed = nothing pending, live = you still owe a click.
  const dirty = !thankYouEqual(draft, saved);

  // Same state the tag below reports, registered with the navigation guard so leaving Settings
  // has to go past it. Discarding is "back to the stored page", which is what this card's
  // baseline already is.
  useUnsavedChanges(dirty, () => setDraft(saved));

  return (
    <Card>
      <Card.Header title={t('settings.thankYouTitle')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Box display="flex" flexDirection="column" gap="1">
            <Title as="h5">{t('settings.thankYouHeading')}</Title>
            <Text fontSize="caption" color="neutral-textLow">
              {t('settings.thankYouHelp')}
            </Text>
          </Box>

          <ToggleControl
            name="thankYouEnabled"
            label={t('settings.thankYouEnable')}
            active={draft.enabled}
            onChange={(v) => set('enabled', v)}
          />

          {draft.enabled && (
            <>
              <Box display="flex" flexDirection="column" gap="2">
                <Text>{t('settings.thankYouMode')}</Text>
                <Segmented
                  value={draft.mode}
                  options={modeOptions}
                  onSelect={(v) => set('mode', v)}
                />
              </Box>

              {draft.mode === 'url' && (
                <TextControl
                  id="thankYouUrl"
                  label={t('settings.thankYouUrlLabel')}
                  value={draft.redirect_url}
                  onChange={(v) => set('redirect_url', v)}
                  helpText={t('settings.thankYouUrlHelp')}
                  error={problem?.field === 'redirect_url' ? t(problem.message) : undefined}
                />
              )}

              {draft.mode === 'whatsapp' && (
                <>
                  <TextControl
                    id="thankYouPhone"
                    label={t('settings.thankYouPhoneLabel')}
                    value={draft.whatsapp_phone}
                    onChange={(v) => set('whatsapp_phone', v)}
                    helpText={t('settings.thankYouPhoneHelp')}
                    error={problem?.field === 'whatsapp_phone' ? t(problem.message) : undefined}
                  />
                  <TextAreaControl
                    id="thankYouMsg"
                    label={t('settings.thankYouMessageLabel')}
                    value={draft.whatsapp_message}
                    onChange={(v) => set('whatsapp_message', v)}
                    helpText={t('settings.thankYouPlaceholders')}
                    lines={4}
                  />
                  {draft.whatsapp_phone.trim() && (
                    <Text fontSize="caption" color="neutral-textLow">
                      {t('settings.thankYouWaPreview')}: https://wa.me/
                      {draft.whatsapp_phone.replace(/\D/g, '')}
                    </Text>
                  )}
                </>
              )}

              {/* The whole page-content group belongs to 'page' mode. The URL and WhatsApp modes
                  are about the handoff button, so a gallery, a preview and a wall of HTML are noise
                  there.
                  🚨 `page_html` still RENDERS above the button in those modes (`showThankYou` in
                  form-renderer passes it to `showHandoff`). Content written in page mode therefore
                  keeps showing after a switch to URL/WhatsApp, without a surface to edit it —
                  switch back to page mode to change or clear it. */}
              {draft.mode === 'page' && (
                <>
                  {/* Ready-made confirmation designs (RCA-1086). Its preview shows whichever
                      template the merchant is BROWSING, which the recipe restore above seeds from
                      their saved page — so on open it is already their own design. A preview of
                      the draft used to sit below this one unconditionally and read as a
                      contradiction: two boxes showing the page, differing whenever the merchant
                      clicked through the gallery without applying anything. The custom branch has
                      its own preview now, but the two are mutually exclusive — a design is
                      selected or the merchant's HTML is. Collapsed by default to keep the section
                      short. */}
                  <Disclosure
                    open={galleryOpen}
                    onToggle={() => setGalleryOpen(!galleryOpen)}
                    label={t('settings.templatesHeading')}
                  >
                    <Text fontSize="caption" color="neutral-textLow">
                      {t('settings.templatesHelp')}
                    </Text>
                    {/* Which design is live right now, so the merchant does not have to click
                        through all five to find out. */}
                    <Box display="flex" alignItems="center" gap="2">
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('settings.templateCurrent')}
                      </Text>
                      <Tag appearance={appliedTemplateId ? 'primary' : 'neutral'}>
                        {appliedTemplateId
                          ? t(TEMPLATE_LABEL[appliedTemplateId])
                          : draft.page_html.trim()
                            ? t('settings.templateCustom')
                            : t('settings.templateNone')}
                      </Tag>
                    </Box>
                    {/* The design choice comes first now that one of its options is "Your own
                        HTML", which the language and call-to-confirm controls do not apply to.
                        Language used to lead, so the cards were already previewed in the buyer's
                        language — it still is, just below the control that decides whether it is
                        shown at all. A picker that appears and disappears above its own trigger is
                        the worse of the two. */}
                    <Segmented
                      value={choice}
                      options={[
                        ...CONFIRMATION_TEMPLATE_IDS.map((id) => ({
                          value: id as TemplateChoice,
                          label: t(TEMPLATE_LABEL[id]),
                        })),
                        { value: CUSTOM_TEMPLATE_CHOICE, label: t('settings.templateCustom') },
                      ]}
                      onSelect={(next) => {
                        pickerTouched.current = true;
                        setChoice(next);
                        if (next === CUSTOM_TEMPLATE_CHOICE) {
                          // Picking "Your own HTML" hides five controls and reveals one, further
                          // down and collapsed. Opening it is the difference between the choice
                          // doing something and appearing to empty the section.
                          setHtmlOpen(true);
                          // Hand back the page they wrote, which "Use this template" overwrote on
                          // the way in. Only when there is one: a merchant who has never written
                          // any is better served starting from the design already in the editor
                          // than from a box we just emptied for them.
                          if (customPageHtml) set('page_html', customPageHtml);
                        }
                      }}
                    />

                    {browsingTemplate ? (
                      <>
                        <Box display="flex" flexDirection="column" gap="2">
                          <Text>{t('settings.templateLanguage')}</Text>
                          <Segmented
                            value={locale}
                            options={TEMPLATE_LOCALE_OPTIONS}
                            onSelect={(next) => {
                              pickerTouched.current = true;
                              setLocaleOverride(next);
                            }}
                          />
                          <Text fontSize="caption" color="neutral-textLow">
                            {t('settings.templateLanguageHelp')}
                          </Text>
                        </Box>
                        <ToggleControl
                          name="templateCallToConfirm"
                          label={t('settings.templateCallToConfirm')}
                          active={callToConfirm}
                          onChange={(next) => {
                            pickerTouched.current = true;
                            setCallToConfirm(next);
                          }}
                        />
                        <Text fontSize="caption" color="neutral-textLow">
                          {t('settings.templateCallToConfirmHelp')}
                        </Text>
                        <PreviewBox html={templateHtml} />
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap="2">
                          {/* Marks the template being browsed as the one already saved in the
                              draft, so the answer is visible without scrolling back to the
                              summary above. */}
                          {choice === appliedTemplateId && (
                            <Tag appearance="success">{t('settings.templateInUse')}</Tag>
                          )}
                          {/* The escape hatch for the editor these five no longer open: copy a
                              design's code, switch to "Your own HTML" and paste it as a starting
                              point. Neutral next to "Use this template", which is the action of
                              this block. */}
                          <Button onClick={() => void copyTemplate()}>
                            {t('settings.templateCopy')}
                          </Button>
                          <Button
                            onClick={() =>
                              draft.page_html.trim() ? setReplaceOpen(true) : applyTemplate()
                            }
                          >
                            {t('settings.templateUse')}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('settings.templateCustomHelp')}
                      </Text>
                    )}
                  </Disclosure>

                  {/* Raw HTML belongs to the "Your own HTML" choice only. Editing a design in
                      place used to be possible and quietly cost the merchant the design: the
                      moment a character changed, `detectConfirmationRecipe` stopped matching, the
                      picker reported "custom" and the language and call-to-confirm toggles no
                      longer did anything to their page. Making it a separate choice means nothing
                      is half a template. */}
                  {!browsingTemplate && (
                    <Disclosure
                      open={htmlOpen}
                      onToggle={() => setHtmlOpen(!htmlOpen)}
                      label={t('settings.thankYouEditHtml')}
                    >
                      <TextAreaControl
                        id="thankYouHtml"
                        label={t('settings.thankYouPageLabel')}
                        value={draft.page_html}
                        onChange={(v) => {
                          // Typed here, it is theirs: it goes to the buyer AND to the stash, so
                          // the next inserted template cannot be the last thing that happened to
                          // their page. Both in one update — two `set` calls would race on the
                          // same draft object and one would be dropped.
                          setDraft((d) => ({ ...d, page_html: v, custom_page_html: v }));
                        }}
                        helpText={t('settings.thankYouPlaceholders')}
                        lines={8}
                        // A whole confirmation page, not a message — it earns more room before it
                        // starts scrolling than the control's default cap allows.
                        maxLines={28}
                      />
                      {/* Directly under the textarea, not up in the gallery: this one repaints as
                          the merchant types, and a preview of your typing that lives in a
                          different collapsible section is a preview you never see move. It is not
                          a second preview either — the gallery's only renders while a design is
                          selected, so exactly one is on screen at a time. */}
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('settings.thankYouPreviewLabel')}
                      </Text>
                      {draft.page_html.trim() ? (
                        <PreviewBox html={draft.page_html} />
                      ) : (
                        // A Text rather than an empty PreviewBox: the placeholder is ours, and
                        // routing it through `dangerouslySetInnerHTML` would make a translation
                        // string into markup for no reason.
                        <Text fontSize="caption" color="neutral-textLow">
                          {t('settings.thankYouPreviewEmpty')}
                        </Text>
                      )}
                    </Disclosure>
                  )}
                </>
              )}
            </>
          )}

          {/* The tag carries the message; the disabled button alone is too quiet to notice, since
              a merchant is not looking at Save when they click "Use this template". */}
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap="2">
            {dirty && !saving && <Tag appearance="warning">{t('common.unsavedChanges')}</Tag>}
            <Button appearance="primary" onClick={() => void onSave()} disabled={saving || !dirty}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </Box>
      </Card.Body>

      {/* Inserting overwrites the whole editor, so hand-written HTML would be lost without asking. */}
      <Modal open={replaceOpen} onDismiss={() => setReplaceOpen(false)}>
        <Modal.Header title={t('settings.templateReplaceTitle')} />
        <Modal.Body padding="none">
          <Text>{t('settings.templateReplaceBody')}</Text>
        </Modal.Body>
        <Modal.Footer>
          <Box display="flex" justifyContent="flex-end" gap="2" width="100%">
            <Button onClick={() => setReplaceOpen(false)}>
              {t('settings.templateReplaceCancel')}
            </Button>
            <Button appearance="danger" onClick={applyTemplate}>
              {t('settings.templateReplaceConfirm')}
            </Button>
          </Box>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

const CART_MODE_OPTIONS: CartMode[] = ['product_only', 'product_and_cart'];
const CART_MODE_LABEL: Record<CartMode, string> = {
  product_only: 'settings.cartModeProductOnly',
  product_and_cart: 'settings.cartModeProductAndCart',
};

export function Settings(): JSX.Element {
  const { t } = useTranslation();
  const { settings, saveSettings, config, saveCartMode } = useAppState();
  const { addToast } = useToast();

  const visibility = settings?.visibility ?? 'product';
  const cartMode = config?.cart_mode ?? 'product_only';

  const onVisibilityChange = async (next: FormVisibility): Promise<void> => {
    if (next === visibility) return;
    const ok = await saveSettings({ visibility: next });
    addToast({
      id: 'save-settings',
      type: ok ? 'success' : 'danger',
      text: ok ? t('common.saved') : t('common.saveError'),
    });
  };

  const onCartModeChange = async (next: CartMode): Promise<void> => {
    if (next === cartMode) return;
    const ok = await saveCartMode(next);
    addToast({
      id: 'save-cart-mode',
      type: ok ? 'success' : 'danger',
      text: ok ? t('common.saved') : t('common.saveError'),
    });
  };

  const disabled = visibility === 'disabled';

  return (
    <Page maxWidth="800px">
      <Page.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Card>
            <Card.Header title={t('settings.visibility')} />
            <Card.Body>
              <Box display="flex" flexDirection="column" gap="4">
                <Box display="flex" flexDirection="column" gap="1">
                  <Title as="h5">{t('settings.visibilityHeading')}</Title>
                  <Text fontSize="caption" color="neutral-textLow">
                    {t('settings.visibilityHelp')}
                  </Text>
                </Box>

                <Segmented
                  value={visibility}
                  options={VISIBILITY_OPTIONS.map((v) => ({ value: v, label: t(OPTION_LABEL[v]) }))}
                  onSelect={(v) => void onVisibilityChange(v)}
                />

                <Box
                  display="flex"
                  gap="2"
                  padding="3"
                  borderRadius="2"
                  backgroundColor={disabled ? 'warning-surface' : 'primary-surface'}
                >
                  <span
                    style={{
                      color: disabled ? '#9a6b00' : '#2e5aac',
                      display: 'flex',
                      flex: '0 0 auto',
                      marginTop: 1,
                    }}
                  >
                    <InfoCircleIcon size="small" />
                  </span>
                  <Box flex="1">
                    <Text
                      fontSize="caption"
                      color={disabled ? 'warning-textHigh' : 'primary-textHigh'}
                    >
                      {t(
                        disabled
                          ? 'settings.visibilityDisabledNote'
                          : 'settings.visibilityEnabledNote',
                      )}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Card.Body>
          </Card>

          {/* The product-page order scope is irrelevant when the form shows only on the cart page. */}
          {visibility !== 'cart' && (
            <Card>
              <Card.Header title={t('settings.cartModeTitle')} />
              <Card.Body>
                <Box display="flex" flexDirection="column" gap="4">
                  <Box display="flex" flexDirection="column" gap="1">
                    <Title as="h5">{t('settings.cartModeHeading')}</Title>
                    <Text fontSize="caption" color="neutral-textLow">
                      {t('settings.cartModeHelp')}
                    </Text>
                  </Box>

                  <Segmented
                    value={cartMode}
                    options={CART_MODE_OPTIONS.map((v) => ({
                      value: v,
                      label: t(CART_MODE_LABEL[v]),
                    }))}
                    onSelect={(v) => void onCartModeChange(v)}
                  />
                </Box>
              </Card.Body>
            </Card>
          )}

          <ThankYouSection />
        </Box>
      </Page.Body>
    </Page>
  );
}
